/**
 * TypeScript types matching backend pipeline models
 */

export interface NodePosition {
  x: number;
  y: number;
}

export interface PipelineNode {
  id: string;
  task_type: string;
  label: string;
  position: NodePosition;
  // Branch nodes store their condition at config.condition; task nodes store
  // execution cardinality at config.cardinality (see StepCardinality).
  config: Record<string, any>;
  agent_id?: string; // Optional custom agent configuration ID
  agent_version?: number; // Pinned agent version (undefined = float to latest)
}

// Whether a step runs once per item in scope, or once across the whole
// resolved batch (e.g. a tracker-wide summarization step). Stored at
// PipelineNode.config.cardinality; defaults to PER_ITEM when unset.
export enum StepCardinality {
  PER_ITEM = 'per_item',
  AGGREGATE = 'aggregate',
  // One instance per (item, related-item) pair discovered by an upstream
  // item_search node this step names in its dependencies.
  PER_RELATED_ITEM = 'per_related_item',
}

export enum EdgeType {
  NORMAL = 'normal',
  LOOP = 'loop',
}

// Search strategy for an item_search node (task_type === 'item_search'),
// stored at PipelineNode.config.search_kind.
export enum SearchKind {
  SIMILARITY = 'similarity',
  REFERENCE_GRAPH = 'reference_graph',
}

export type ConditionOperator = 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than' | 'exists';

// Condition used to filter items while iterating a loop edge.
export interface LoopFilterCondition {
  field: string;
  operator: ConditionOperator;
  value: any;
}

export interface EdgeLoopConfig {
  iterate_over: string; // Field path to iterate over
  filter?: LoopFilterCondition;
}

// Condition evaluated by a branch node (task_type === 'branch'), stored in
// PipelineNode.config.condition. upstream_step_reference is the sanitized
// step name of the connected upstream node (see pipelineToWorkflow).
export interface StepCondition {
  upstream_step_reference: string;
  field: string;
  operator: ConditionOperator;
  value: any;
}

export interface PipelineEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  edge_type?: EdgeType;
  loop_config?: EdgeLoopConfig;
  // Which named handle on the source node this edge originates from.
  // Set for edges leaving a branch node ('true'/'false'); undefined otherwise.
  source_handle?: 'true' | 'false' | null;
}

export interface PipelineMetadata {
  name: string;
  description?: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  created_by: string;
  // Backend-specific fields preserved for round-trip updates
  slug?: string;
  version?: number;
  inputs?: any[];  // WorkflowInput array from backend
}

export interface PipelineDefinition {
  id: string;
  metadata: PipelineMetadata;
  nodes: PipelineNode[];
  edges: PipelineEdge[];
  // Populated by listPipelines(), which returns lightweight summaries with
  // nodes/edges left empty; getPipeline() returns full nodes/edges instead.
  node_count?: number;
  edge_count?: number;
}

export enum ExecutionStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export interface StepInstanceExecution {
  item_key: string;
  task_id: string | null;
  status: ExecutionStatus;
  rendered_system_prompt: string | null;
  rendered_user_prompt: string | null;
  inputs: Record<string, any> | null;
  result: Record<string, any> | null;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface NodeExecution {
  node_id: string;
  status: ExecutionStatus;
  instances: StepInstanceExecution[];
  // Legacy single-value fields consumed by the List tab; derived from the
  // relevant instance in getExecutionStatus() (api/client.ts) for backward compat.
  started_at?: string;
  completed_at?: string;
  result?: Record<string, any>;
  error?: string;
  task_id?: string;
  progress_message?: string;
}

export interface PipelineExecution {
  id: string;
  pipeline_id: string;
  status: ExecutionStatus;
  started_at: string;
  completed_at?: string;
  // The resolved scope's item id(s) for the whole execution - used to derive
  // data source node status (see ExecutionMonitor.tsx's nodeStatuses).
  item_ids: string[] | null;
  node_executions: Record<string, NodeExecution>;
}

export interface TaskTypeInfo {
  id: string;
  label: string;
  category: string;
  description: string;
}
